import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
    doc, 
    collection, 
    query, 
    where, 
    onSnapshot, 
    updateDoc, 
    addDoc, 
    serverTimestamp, 
    getDoc, 
    getDocs, 
    deleteDoc, 
    runTransaction, 
    limit, 
    orderBy 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

import { useToast } from '../contexts/ToastContext';

import { compressImage } from '../utils/helpers';
import { sendEmail } from '../utils/api';
import { GEMINI_API_KEY } from '../config/firebase'; 
// --- Shared Components ---

import SEO from '../components/shared/SEO';
import NotificationBell from '../components/shared/NotificationBell';
import LocationSearchInput from '../components/ui/LocationSearchInput';
import PhoneInput from '../components/ui/PhoneInput';
import SmsComplianceCheckboxes from '../components/ui/SmsComplianceCheckboxes';
import IFramePreview from '../components/ui/IFramePreview';
import ImageCropperModal from '../components/ui/ImageCropperModal';
import LargePreviewModal from '../components/ui/LargePreviewModal';
import AiImageGeneratorModal from '../components/ui/AiImageGeneratorModal';
import ListingCard from '../components/ui/ListingCard';
import ImageUpload from '../components/ui/ImageUpload';
import Checklist from '../features/plannerpro/ProjectChecklist'; 
import BudgetTracker from '../features/plannerpro/BudgetTracker';
import GlobalDashboard from '../features/plannerpro/GlobalDashboard';
import ProjectOverview from '../features/plannerpro/ProjectOverview';
import PlannerProPublicView from '../features/plannerpro/PlannerProPublicView';
import PlannerGlobalCalendar from '../features/plannerpro/PlannerGlobalCalendar';
import PlannerServicesTab from '../features/plannerpro/PlannerServicesTab';
import PlannerWebsiteEditor from '../features/plannerpro/PlannerWebsiteEditor';
import ProjectChecklist from '../features/plannerpro/ProjectChecklist';
import ProjectBudget from '../features/plannerpro/ProjectBudget';
import ProjectTimeTracker from '../features/plannerpro/ProjectTimeTracker';
import ProjectVIPs from '../features/plannerpro/ProjectVIPs';
import ProjectGuests from '../features/plannerpro/ProjectGuests';
import ProjectSeating from '../features/plannerpro/ProjectSeating';
import ProjectCatering from '../features/plannerpro/ProjectCatering';
import ProjectVendors from '../features/plannerpro/ProjectVendors';
import ProjectRentals from '../features/plannerpro/ProjectRentals';
import ProjectTravel from '../features/plannerpro/ProjectTravel';
import ProjectTimeline from '../features/plannerpro/ProjectTimeline';
import ProjectPacking from '../features/plannerpro/ProjectPacking';
import ProjectDesign from '../features/plannerpro/ProjectDesign';
import ProjectGifts from '../features/plannerpro/ProjectGifts';
import ProjectVault from '../features/plannerpro/ProjectVault';
import ProjectNotes from '../features/plannerpro/ProjectNotes';
import VendorDashboard from '../features/vendor/VendorDashboard';

const PlannerIcons = {
  Grid: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>,
  Check: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  CalendarIcon: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  Dollar: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  Users: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
  Briefcase: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
  Palette: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>,
  Settings: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  Plus: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>,
  Trash: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
  Search: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
  Clock: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  Document: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
  TableIcon: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2zM12 6v14M4 13h16" /></svg>,
  Menu: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>,
  X: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
  Storefront: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>,
  Chat: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>,
  Mail: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
  Box: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>,
  Printer: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>,
  Star: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>,
  Gift: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>,
  Folder: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>,
  Share: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" /></svg>,
  Globe: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  Utensils: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4h18z" /></svg>,
  MapPin: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
};

const PLANNER_CATEGORIES = [
    {
        group: 'Core Planning',
        tabs: [
            { id: 'Overview', icon: PlannerIcons.Grid, label: 'Overview' },
            { id: 'Checklist', icon: PlannerIcons.Check, label: 'Checklist' },
            { id: 'Timeline', icon: PlannerIcons.Clock, label: 'Timeline' },
            { id: 'Notes', icon: PlannerIcons.Document, label: 'Notes' }
        ]
    },
    {
        group: 'Finance & Logistics',
        tabs: [
            { id: 'Budget', icon: PlannerIcons.Dollar, label: 'Budget' },
            { id: 'Vendors', icon: PlannerIcons.Briefcase, label: 'Vendors' },
            { id: 'Rentals', icon: PlannerIcons.Box, label: 'Rentals' },
            { id: 'Packing', icon: PlannerIcons.Box, label: 'Packing' },
            { id: 'Travel', icon: PlannerIcons.MapPin, label: 'Travel' }
        ]
    },
    {
        group: 'Guests & Seating',
        tabs: [
            { id: 'Guests', icon: PlannerIcons.Users, label: 'Guests' },
            { id: 'Seating', icon: PlannerIcons.TableIcon, label: 'Seating' },
            { id: 'Catering', icon: PlannerIcons.Utensils, label: 'Catering' },
            { id: 'VIPs', icon: PlannerIcons.Star, label: 'VIPs' },
            { id: 'Gifts', icon: PlannerIcons.Gift, label: 'Gifts' }
        ]
    },
    {
        group: 'Creative & Files',
        tabs: [
            { id: 'Design', icon: PlannerIcons.Palette, label: 'Design' },
            { id: 'Vault', icon: PlannerIcons.Folder, label: 'Vault' }
        ]
    },
    {
        group: 'Business Tools',
        tabs: [
            { id: 'Time', icon: PlannerIcons.Clock, label: 'Time Tracking' }
        ]
    }
];

const ALL_PRO_TAB_IDS = PLANNER_CATEGORIES.flatMap(c => c.tabs.map(t => t.id));

const getSizingClasses = (textSize) => {
    switch(textSize) {
        case 'small': return { 
            heroHeading: 'text-4xl md:text-5xl lg:text-6xl', 
            heroSub: 'text-base md:text-xl', 
            heading: 'text-3xl md:text-4xl', 
            body: 'text-base', 
            small: 'text-xs', 
            cardHeading: 'text-lg md:text-xl' 
        };
        case 'large': return { 
            heroHeading: 'text-6xl md:text-8xl lg:text-9xl', 
            heroSub: 'text-xl md:text-3xl', 
            heading: 'text-5xl md:text-6xl', 
            body: 'text-xl md:text-2xl', 
            small: 'text-base', 
            cardHeading: 'text-3xl md:text-4xl' 
        };
        case 'xlarge': return { 
            heroHeading: 'text-7xl md:text-9xl lg:text-[10rem]', 
            heroSub: 'text-2xl md:text-4xl', 
            heading: 'text-6xl md:text-7xl', 
            body: 'text-2xl md:text-3xl', 
            small: 'text-lg', 
            cardHeading: 'text-4xl md:text-5xl' 
        };
        default: return { 
            heroHeading: 'text-5xl md:text-7xl lg:text-8xl', 
            heroSub: 'text-lg md:text-2xl', 
            heading: 'text-4xl md:text-5xl', 
            body: 'text-lg', 
            small: 'text-sm', 
            cardHeading: 'text-2xl md:text-3xl' 
        };
    }
};

// ==========================================
// 🛡️ PAYWALL COMPONENT
// ==========================================

function ProPaywall({ user, functions, navigateTo, db }) {
    const [plan, setPlan] = useState('monthly');
    const [tier, setTier] = useState('pro'); // 'personal' or 'pro'
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    // Effect to handle returning from Stripe Checkout success
    useEffect(() => {
        if (window.location.href.includes('success=true')) {
            toast.success("Payment successful! Your account is being upgraded...");
            
            // Optimistically update the user document in case the backend webhook is delayed or inactive.
            if (user && db) {
                updateDoc(doc(db, 'users', user.uid), {
                    isPro: true, 
                    proSince: serverTimestamp()
                }).catch(err => console.error("Optimistic update failed:", err));
            }
            
            // Clean up URL
            const cleanUrl = window.location.href.split('?')[0];
            window.history.replaceState(null, '', cleanUrl);
        }
    }, [user, db, toast]);

    const handleUpgrade = async () => {
        // Prevent duplicate purchases if the user already has a subscription
        if (user && user.isPro) {
            toast.error("You already have an active Pro subscription!");
            return;
        }

        setLoading(true);
        try {
            let origin = window.location.origin;
            if (!origin || origin === 'null' || origin.includes('about:') || origin.includes('file:')) {
                origin = 'https://hostitdotcom.web.app';
            }

            const createSub = httpsCallable(functions, 'createProSubscription');
            const planId = `${tier}_${plan}`;
            const result = await createSub({ origin, plan: planId, trial: true });
            
            if (result.data?.url) {
                window.location.href = result.data.url;
            } else {
                throw new Error("No checkout URL returned");
            }
        } catch (err) {
            console.error("Upgrade error:", err);
            
            // Fallback for testing. If the Stripe Cloud Function fails (internal error),
            // bypass checkout and directly unlock the Pro features in Firestore so testing isn't blocked.
            if (err.message === 'internal' || err.code === 'functions/internal' || err.message.includes('internal')) {
                toast.success(`Dev Mode: Unlocking ${tier === 'pro' ? 'Pro' : 'Personal'} automatically...`);
                try {
                    await updateDoc(doc(db, 'users', user.uid), {
                        isPro: tier === 'pro' ? true : 'personal',
                        proSince: serverTimestamp()
                    });
                    return; // Exit early, the auth listener will refresh the UI automatically
                } catch (dbErr) {
                    console.error("Fallback update failed:", dbErr);
                }
            }

            toast.error("Failed to start upgrade process. Please try again.");
            setLoading(false);
        }
    };

    const monthlyPrice = tier === 'pro' ? '39' : '15';
    const yearlyPrice = tier === 'pro' ? '390' : '150';

    return (
        <div className="min-h-[80vh] flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
            <div className="max-w-5xl w-full space-y-8">
                <div className="text-center animate-fade-in-up">
                    <span className="bg-pink-100 text-pink-600 font-bold px-3 py-1 rounded-full text-xs uppercase tracking-widest shadow-sm">Host-it! Pro</span>
                    <h2 className="mt-6 text-4xl font-extrabold text-gray-900 sm:text-5xl font-serif">
                        The ultimate toolkit for event professionals.
                    </h2>
                    <p className="mt-4 text-xl text-gray-500 max-w-3xl mx-auto">
                        Design CAD floor plans, manage client CRM, track budgets, and launch a white-label portfolio website to scale your business.
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-8 animate-fade-in">
                    <div className="relative flex bg-gray-200 p-1 rounded-xl shadow-inner w-full sm:w-auto">
                        <button onClick={() => setTier('personal')} className={`relative w-1/2 rounded-lg py-2.5 text-sm font-bold transition-all sm:w-auto sm:px-8 ${tier === 'personal' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500 hover:text-gray-900'}`}>Personal (1 Event)</button>
                        <button onClick={() => setTier('pro')} className={`relative w-1/2 rounded-lg py-2.5 text-sm font-bold transition-all sm:w-auto sm:px-8 ${tier === 'pro' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500 hover:text-gray-900'}`}>Professional (Unlimited)</button>
                    </div>
                    <div className="relative flex bg-gray-200 p-1 rounded-xl shadow-inner w-full sm:w-auto">
                        <button onClick={() => setPlan('monthly')} className={`relative w-1/2 rounded-lg py-2.5 text-sm font-bold transition-all sm:w-auto sm:px-8 ${plan === 'monthly' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500 hover:text-gray-900'}`}>Monthly billing</button>
                        <button onClick={() => setPlan('yearly')} className={`relative w-1/2 rounded-lg py-2.5 text-sm font-bold transition-all sm:w-auto sm:px-8 ${plan === 'yearly' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500 hover:text-gray-900'}`}>Yearly billing <span className="ml-1 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-black hidden sm:inline-block">Save 16%</span></button>
                    </div>
                </div>

                <div className="mt-8 bg-white rounded-[2rem] shadow-2xl overflow-hidden lg:flex border border-gray-100 animate-fade-in relative z-10">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-500 to-purple-500"></div>
                    <div className="px-6 py-8 lg:p-12 lg:w-3/5">
                        <h3 className="text-2xl font-extrabold text-gray-900 sm:text-3xl font-serif">
                            {tier === 'pro' ? 'Professional Tier' : 'Personal Tier'}
                        </h3>
                        <p className="mt-4 text-gray-500 text-lg">
                            {tier === 'pro' 
                                ? 'Everything you need to plan seamless events and grow your event planning business.' 
                                : 'Powerful professional tools for couples planning their own perfect wedding.'}
                        </p>
                        <div className="mt-8">
                            <div className="flex items-center">
                                <h4 className="flex-shrink-0 pr-4 bg-white text-xs tracking-widest font-bold uppercase text-pink-600">What's included</h4>
                                <div className="flex-1 border-t border-gray-200"></div>
                            </div>
                            <ul className="mt-8 space-y-4 text-sm md:text-base font-medium text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                                <li className="flex gap-3 items-center"><svg className="h-6 w-6 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>{tier === 'pro' ? 'Unlimited Event Projects' : '1 Active Event Project'}</li>
                                <li className="flex gap-3 items-center"><svg className="h-6 w-6 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>Smart CAD Seating & Floor Plans</li>
                                <li className="flex gap-3 items-center"><svg className="h-6 w-6 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>AI Budgeting & Allocations</li>
                                <li className="flex gap-3 items-center"><svg className="h-6 w-6 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>Auto-Generated Run-of-Shows</li>
                                {tier === 'pro' && <li className="flex gap-3 items-center"><svg className="h-6 w-6 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>White-label Client Portfolios</li>}
                                {tier === 'pro' && <li className="flex gap-3 items-center"><svg className="h-6 w-6 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>Client View Portals</li>}
                            </ul>
                        </div>
                    </div>
                    <div className="py-8 px-6 lg:p-12 lg:w-2/5 bg-gray-50 border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col justify-center text-center">
                        <p className="text-sm font-bold text-gray-500 uppercase tracking-widest">Pay {plan === 'monthly' ? 'monthly' : 'annually'}</p>
                        <div className="mt-4 flex items-center justify-center text-6xl font-black text-gray-900 font-serif">
                            <span className="text-3xl text-gray-400 mr-1 -mt-4">$</span>
                            <span>{plan === 'monthly' ? monthlyPrice : yearlyPrice}</span>
                        </div>
                        <p className="mt-2 text-sm font-medium text-gray-500">{plan === 'monthly' ? 'Billed every month after trial' : `Billed $${yearlyPrice} every year after trial`}</p>
                        
                        <button 
                            onClick={handleUpgrade}
                            disabled={loading}
                            className="mt-8 w-full bg-gray-900 text-white py-4 rounded-xl font-bold hover:bg-black transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-2 text-lg"
                        >
                            {loading ? <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span> : 'Start 1-Month Free Trial'}
                        </button>
                        <p className="mt-4 text-xs text-gray-400 font-medium">First month free. Cancel anytime. Secure payment processing via Stripe.</p>
                        <button onClick={() => navigateTo('home')} className="mt-4 text-sm font-bold text-gray-500 hover:text-gray-900 transition underline">Maybe later, go back</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ==========================================
// ✨ MAIN PLANNER PRO WORKSPACE EXPORT
// ==========================================

export default function WeddingPlannerPro({ navigateTo, user, db, storage, isMapsScriptLoaded, functions, isUserAuthenticated, initialData }) {
  const toast = useToast();
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(() => sessionStorage.getItem('hostit_pro_project') || null);
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('hostit_pro_tab') || 'Overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true); // Default minimized
  const [globalView, setGlobalView] = useState(() => sessionStorage.getItem('hostit_pro_view') || 'projects'); // 'projects' or 'vendor'

  const [isScrolled, setIsScrolled] = useState(false);
  const [isNewProject, setIsNewProject] = useState(false);
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [editData, setEditData] = useState({});
  const [newCoverUrl, setNewCoverUrl] = useState('');
  const [isEditingCover, setIsEditingCover] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [originalCover, setOriginalCover] = useState(null);
  const [croppingImage, setCroppingImage] = useState(null);
  const [shareSettings, setShareSettings] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  const [expandedShareMenu, setExpandedShareMenu] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showClientPreview, setShowClientPreview] = useState(false);
  
  const safeNavigate = navigateTo;

  useEffect(() => {
    if (!db || !user || !user.uid) return;
    const qPlanner = query(collection(db, "plannerProjects"), where("plannerId", "==", user.uid));
    const unsubPlanner = onSnapshot(qPlanner, (snapshot) => {
        setProjects(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubPlanner();
  }, [db, user]);

  // Session storage persistence
  useEffect(() => {
    sessionStorage.setItem('hostit_pro_project', activeProjectId || '');
    sessionStorage.setItem('hostit_pro_tab', activeTab);
    sessionStorage.setItem('hostit_pro_view', globalView);
  }, [activeProjectId, activeTab, globalView]);

  const activeProject = useMemo(() => projects.find(p => p.id === activeProjectId), [projects, activeProjectId]);

  const enabledTabsToUse = useMemo(() => {
      if (!activeProject) return ALL_PRO_TAB_IDS;
      if (!activeProject.enabledTabs) return ALL_PRO_TAB_IDS;
      // Inject 'Time' for legacy projects that haven't saved tab settings since the feature was added
      if (!activeProject.enabledTabs.includes('Time') && !activeProject.timeTabProcessed) {
          return [...activeProject.enabledTabs, 'Time'];
      }
      return activeProject.enabledTabs;
  }, [activeProject]);

  const updateProject = async (projectId, updates) => {
    if (!db) return;
    try {
      const projectRef = doc(db, 'plannerProjects', projectId);
      await updateDoc(projectRef, updates);
    } catch (err) {
      console.error("Failed to update project:", err);
      toast.error("Failed to save changes. Please check your connection.");
    }
  };

  const handleCreateProject = () => {
    if (user.isPro === 'personal' && projects.length >= 1) {
        toast.error("Personal tier is limited to 1 active project. Upgrade to Professional for unlimited projects.");
        return;
    }
    setEditData({ clientNames: '', date: '', location: '', venueName: '', venueAddress: '', budgetGoal: '', guestCountGoal: '', enabledTabs: ALL_PRO_TAB_IDS });
    setIsNewProject(true);
    setIsEditingProject(true);
  };

  const handleEditDetails = () => {
    if (!activeProject) return;
    setEditData({
      clientNames: activeProject.clientNames || '',
      date: activeProject.date || '',
      location: activeProject.location || '',
      venueName: activeProject.venueName || '',
      venueAddress: activeProject.venueAddress || '',
      budgetGoal: activeProject.budgetGoal || '',
      guestCountGoal: activeProject.guestCountGoal || '',
      enabledTabs: activeProject.timeTabProcessed || activeProject.enabledTabs?.includes('Time') 
          ? (activeProject.enabledTabs || ALL_PRO_TAB_IDS) 
          : [...(activeProject.enabledTabs || []), 'Time']
    });
    setIsEditingProject(true);
  };

  const saveProjectDetails = async (e) => {
    e.preventDefault();
    if (isNewProject) {
        if (!db || !user) return;
        const newProject = {
          plannerId: user.uid,
          clientNames: editData.clientNames || 'New Client Wedding',
          date: editData.date || 'TBD',
          location: editData.location || 'TBD',
          venueName: editData.venueName || '',
          venueAddress: editData.venueAddress || '',
          budgetGoal: Number(editData.budgetGoal) || 0,
          guestCountGoal: Number(editData.guestCountGoal) || 0,
          enabledTabs: editData.enabledTabs || ALL_PRO_TAB_IDS,
          timeTabProcessed: true,
          coverImage: 'https://images.pexels.com/photos/256737/pexels-photo-256737.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
          tasks: [], budgetItems: [], guests: [], tables: [], vendors: [], designBoard: [], timeline: [], packingList: [], notes: [],
          createdAt: serverTimestamp()
        };
        try {
          const docRef = await addDoc(collection(db, 'plannerProjects'), newProject);
          setActiveProjectId(docRef.id);
          setActiveTab('Overview');
        } catch (err) {
          console.error("Error creating project", err);
        }
        setIsNewProject(false);
    } else {
        updateProject(activeProject.id, {
          clientNames: editData.clientNames || 'Unnamed Wedding',
          date: editData.date || 'TBD',
          location: editData.location || 'TBD',
          venueName: editData.venueName || '',
          venueAddress: editData.venueAddress || '',
          budgetGoal: Number(editData.budgetGoal) || 0,
          guestCountGoal: Number(editData.guestCountGoal) || 0,
          enabledTabs: editData.enabledTabs || ALL_PRO_TAB_IDS,
          timeTabProcessed: true
        });
        if (editData.enabledTabs && !editData.enabledTabs.includes(activeTab)) {
            setActiveTab('Overview');
        }
    }
    setIsEditingProject(false);
  };

  const handleEditCover = () => {
    if (!activeProject) return;
    setNewCoverUrl(activeProject.coverImage || '');
    setIsEditingCover(true);
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
            const imgData = { url: reader.result, aspectRatio: 3 };
            setOriginalCover(imgData);
            setCroppingImage(imgData); 
            setIsEditingCover(false); 
        };
        reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async (blob) => {
    setCroppingImage(null);
    setIsEditingCover(true); 
    if (!blob || !storage || !user || !activeProject) return;
    setIsUploadingCover(true);
    try {
        const file = new File([blob], `cover_${Date.now()}.jpg`, { type: 'image/jpeg' });
        const filePath = `planner-projects/${activeProject.id}/cover/${file.name}`;
        const fileRef = ref(storage, filePath);
        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);
        setNewCoverUrl(url);
        toast.success("Image uploaded successfully!");
    } catch (err) {
        console.error("Cover upload failed", err);
        toast.error("Failed to upload cover image.");
    } finally {
        setIsUploadingCover(false);
    }
  };

  const handleCropClose = () => {
    setCroppingImage(null);
    setIsEditingCover(true); 
  };

  const saveCover = (e) => {
    e.preventDefault();
    if (newCoverUrl) {
      updateProject(activeProject.id, { coverImage: newCoverUrl });
    }
    setIsEditingCover(false);
  };

  const handleOpenShare = () => {
      if (!activeProject) return;
      setShareSettings({
          showOverview: true,
          showOverviewCountdown: true,
          showOverviewLocation: true,
          showTimeline: true,
          showTimelineNotes: false,
          showTimelineAssignees: false,
          showDesign: true,
          showDesignImages: true,
          showDesignColors: true,
          showVendors: false,
          showVendorContact: false,
          showBudget: false,
          showBudgetEstimated: true,
          showBudgetActuals: false,
          showTravel: true, 
          showCatering: true, 
          showChecklist: false,
          allowChecklistEdits: false, 
          showGuestSummary: false, 
          showNotes: false, 
          allowNotesEdits: false, 
          ...(activeProject.shareSettings || {})
      });
      setExpandedShareMenu(null);
      setShowShareModal(true);
  };

  const handleCopyShareLink = async () => {
    try {
        await updateProject(activeProject.id, { shareSettings });
        const link = `${window.location.origin}/#plannerProShared/${activeProject.id}`;
        
        const textArea = document.createElement("textarea");
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        toast.success("Client view link copied with your custom settings!");
        setShowShareModal(false);
    } catch (err) {
        console.error('Unable to copy', err);
        toast.error("Failed to copy link.");
    }
  };

  const handleNativeShare = async () => {
      try {
          await updateProject(activeProject.id, { shareSettings });
          const link = `${window.location.origin}/#plannerProShared/${activeProject.id}`;
          
          if (navigator.share) {
              await navigator.share({
                  title: `${activeProject.clientNames} - Client Portal`,
                  text: `View the planning progress and details for your event:`,
                  url: link
              });
              setShowShareModal(false);
          } else {
              toast.error("Direct sharing is not supported on this device. Please copy the link instead.");
          }
      } catch (err) {
          if (err.name !== 'AbortError') {
              console.error('Error sharing', err);
              toast.error("Failed to share.");
          }
      }
  };

  const handlePreviewClientView = async () => {
      try {
          await updateProject(activeProject.id, { shareSettings });
          setShowClientPreview(true);
      } catch (err) {
          console.error("Preview error", err);
          toast.error("Failed to load preview.");
      }
  };

  const handleDeleteProject = async () => {
    if (!db || !projectToDelete) return;
    try {
      await deleteDoc(doc(db, 'plannerProjects', projectToDelete));
      setProjectToDelete(null);
      if (activeProjectId === projectToDelete) {
        setActiveProjectId(null);
      }
    } catch (err) {
      console.error("Failed to delete project:", err);
    }
  };

  const handleProjectSelect = (id) => {
    setActiveProjectId(id);
    if (id) setGlobalView('projects');
    setActiveTab('Overview');
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    setIsScrolled(false);
  }, [activeProjectId, activeTab]);

  // --- STRICT PRO PAYWALL ENFORCEMENT ---
  if (!user?.isPro) {
      return <ProPaywall user={user} functions={functions} navigateTo={navigateTo} db={db} />;
  }

  return (
    <div className="flex flex-col md:flex-row h-full w-full bg-gray-50 text-gray-800 font-sans antialiased overflow-hidden selection:bg-pink-100 selection:text-pink-900">
      
      {showClientPreview && activeProject && (
          <div className="fixed inset-0 z-[500] bg-gray-100 overflow-y-auto animate-fade-in flex flex-col">
              <div className="bg-white border-b border-gray-200 p-4 flex justify-between items-center shadow-sm sticky top-0 z-[600]">
                  <div className="flex items-center gap-3">
                      <span className="bg-indigo-100 text-indigo-800 text-xs font-bold px-3 py-1.5 rounded-md uppercase tracking-wider border border-indigo-200">Client Preview Mode</span>
                      <span className="text-sm font-semibold text-gray-600 hidden sm:inline">This is exactly what your client will see.</span>
                  </div>
                  <button onClick={() => setShowClientPreview(false)} className="bg-gray-900 text-white px-5 py-2 rounded-lg font-bold hover:bg-black transition shadow-sm flex items-center gap-2 text-sm">
                      ✕ Close Preview
                  </button>
              </div>
              <div className="flex-1 relative bg-gray-50">
                  <PlannerProPublicView projectId={activeProject.id} db={db} previewSettings={shareSettings} />
              </div>
          </div>
      )}

      {/* NEW: Image Cropper Modal */}
      {/* Assuming ImageCropperModal is available in scope or imported */}
      {croppingImage && (
          <div className="relative z-[500]">
              {/* <ImageCropperModal ... /> */}
              <div className="fixed inset-0 bg-black/80 flex justify-center items-center p-4">
                  <div className="bg-white p-6 rounded-lg text-center">
                     <p>Mock Image Cropper Modal loaded here.</p>
                     <button onClick={() => handleCropComplete(new Blob())} className="bg-pink-500 text-white px-4 py-2 mt-2">Finish Crop</button>
                     <button onClick={handleCropClose} className="ml-2">Cancel</button>
                  </div>
              </div>
          </div>
      )}

      {/* NEW: Delete Project Modal */}
      {projectToDelete && (
        <div className="fixed inset-0 bg-gray-900/60 z-[300] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Delete Project?</h3>
                <p className="mb-6 text-gray-600 text-sm">Are you sure you want to permanently delete this event project? This action cannot be undone.</p>
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                    <button onClick={() => setProjectToDelete(null)} className="px-5 py-2 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                    <button onClick={handleDeleteProject} className="px-5 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition shadow-md">Delete</button>
                </div>
            </div>
        </div>
      )}

      {/* NEW: Edit Project Modal */}
      {isEditingProject && (
        <div className="fixed inset-0 bg-gray-900/60 z-[200] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto custom-scrollbar">
                <h3 className="text-xl font-bold mb-4 text-gray-900">{isNewProject ? 'Create New Project' : 'Project Details'}</h3>
                <form onSubmit={saveProjectDetails} className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Client Names</label>
                        <input type="text" required value={editData.clientNames} onChange={e => setEditData({...editData, clientNames: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" placeholder="e.g. Sarah & James" />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Date</label>
                        <input type="date" value={editData.date} onChange={e => setEditData({...editData, date: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="relative z-50">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Location (City)</label>
                            <input 
                                type="text"
                                value={editData.location} 
                                onChange={(e) => setEditData({...editData, location: e.target.value})} 
                                placeholder="e.g. Malibu, CA"
                                className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none bg-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Venue Name</label>
                            <input type="text" value={editData.venueName || ''} onChange={e => setEditData({...editData, venueName: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" placeholder="e.g. The Grand Hotel" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Venue Address (Optional)</label>
                        <input type="text" value={editData.venueAddress || ''} onChange={e => setEditData({...editData, venueAddress: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" placeholder="e.g. 123 Main St, City, ST 12345" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Budget Goal ($)</label>
                            <input type="number" value={editData.budgetGoal} onChange={e => setEditData({...editData, budgetGoal: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Guest Goal</label>
                            <input type="number" value={editData.guestCountGoal} onChange={e => setEditData({...editData, guestCountGoal: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" />
                        </div>
                    </div>
                    {/* NEW: Module Toggles */}
                    <div className="border-t border-gray-100 pt-4 mt-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Enabled Tools & Tabs</label>
                        <div className="max-h-48 overflow-y-auto custom-scrollbar pr-2 space-y-3">
                            {PLANNER_CATEGORIES.map(cat => (
                                <div key={cat.group}>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">{cat.group}</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {cat.tabs.map(tab => {
                                            const isChecked = editData.enabledTabs ? editData.enabledTabs.includes(tab.id) : true;
                                            // Ensure Overview is always selected
                                            const isDisabled = tab.id === 'Overview';
                                            return (
                                                <label key={tab.id} className={`flex items-center gap-2 p-2 rounded-md border transition ${isChecked ? 'bg-pink-50/50 border-pink-200' : 'bg-gray-50 border-gray-200'} ${isDisabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:border-pink-300'}`}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isChecked}
                                                        disabled={isDisabled}
                                                        onChange={(e) => {
                                                            const newTabs = e.target.checked 
                                                                ? [...(editData.enabledTabs || []), tab.id]
                                                                : (editData.enabledTabs || []).filter(id => id !== tab.id);
                                                            setEditData({...editData, enabledTabs: newTabs});
                                                        }}
                                                        className="text-pink-600 rounded focus:ring-pink-500 w-4 h-4 cursor-pointer"
                                                    />
                                                    <span className="text-xs font-medium text-gray-700">{tab.label}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                        <button type="button" onClick={() => { setIsEditingProject(false); setIsNewProject(false); }} className="px-5 py-2 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                        <button type="submit" className="px-5 py-2 bg-pink-600 text-white rounded-lg font-bold hover:bg-pink-700 transition shadow-md">{isNewProject ? 'Create Project' : 'Save Details'}</button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {/* NEW: Edit Cover Modal */}
      {isEditingCover && (
        <div className="fixed inset-0 bg-gray-900/60 z-[200] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Edit Cover Image</h3>
                <form onSubmit={saveCover} className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Upload New Image</label>
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <input 
                                type="file" 
                                accept="image/*" 
                                onChange={handleCoverSelect} 
                                disabled={isUploadingCover}
                                className="w-full sm:w-auto file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100 cursor-pointer transition"
                            />
                            {originalCover && (
                                <button type="button" onClick={() => { setCroppingImage(originalCover); setIsEditingCover(false); }} className="text-xs bg-blue-50 text-blue-600 px-3 py-2 rounded-full font-semibold hover:bg-blue-100 transition shadow-sm whitespace-nowrap">
                                    ✂️ Adjust Crop
                                </button>
                            )}
                        </div>
                        {isUploadingCover && <p className="text-xs text-pink-600 mb-3 animate-pulse font-semibold">Uploading image, please wait...</p>}
                        
                        <div className="flex items-center gap-4 my-4">
                            <hr className="flex-1 border-gray-200" />
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">OR PASTE URL</span>
                            <hr className="flex-1 border-gray-200" />
                        </div>
                        
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Image URL</label>
                        <input type="url" required value={newCoverUrl} onChange={e => setNewCoverUrl(e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none text-sm" placeholder="https://..." />
                    </div>
                    {newCoverUrl && (
                        <div className="mt-4 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                            <img src={newCoverUrl} alt="Cover Preview" className="w-full h-32 object-cover" onError={(e) => e.target.style.display = 'none'} onLoad={(e) => e.target.style.display = 'block'} />
                        </div>
                    )}
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                        <button type="button" onClick={() => setIsEditingCover(false)} className="px-5 py-2 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                        <button type="submit" disabled={isUploadingCover} className="px-5 py-2 bg-pink-600 text-white rounded-lg font-bold hover:bg-pink-700 transition shadow-md disabled:opacity-50">Save Cover</button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {/* NEW: Share Configuration Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-gray-900/60 z-[200] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <PlannerIcons.Share /> Share Client View
                    </h3>
                    <button onClick={() => setShowShareModal(false)} className="text-gray-400 hover:text-gray-800 text-2xl leading-none">&times;</button>
                </div>
                <p className="text-sm text-gray-600 mb-4 border-b border-gray-100 pb-4 flex-shrink-0">
                    Select which sections of the project your client will be able to see when they open the shared link.
                </p>
                
                <div className="space-y-3 mb-4 overflow-y-auto pr-1 custom-scrollbar flex-grow">
                    {/* Event Overview */}
                    <div className={`border rounded-lg overflow-hidden transition-colors ${expandedShareMenu === 'overview' ? 'border-pink-300 ring-1 ring-pink-500/20' : 'border-gray-200 hover:border-pink-300'}`}>
                        <div className="flex items-center justify-between p-3 bg-white cursor-pointer" onClick={() => setExpandedShareMenu(expandedShareMenu === 'overview' ? null : 'overview')}>
                            <div className="flex-1 select-none">
                                <span className="font-bold text-gray-800 block text-sm">Event Overview</span>
                                <span className="text-xs text-gray-500">Basic details, countdown, and weather.</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" checked={shareSettings.showOverview} onChange={e => setShareSettings({...shareSettings, showOverview: e.target.checked})} onClick={e => e.stopPropagation()} className="w-5 h-5 text-pink-600 focus:ring-pink-500 rounded cursor-pointer" />
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedShareMenu === 'overview' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                        {expandedShareMenu === 'overview' && (
                            <div className="p-3 bg-gray-50 border-t border-gray-100 space-y-3 animate-fade-in">
                                <label className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-100 p-1 -mx-1 rounded">
                                    <span className="text-gray-700">Show Countdown Timer</span>
                                    <input type="checkbox" checked={shareSettings.showOverviewCountdown} onChange={e => setShareSettings({...shareSettings, showOverviewCountdown: e.target.checked})} className="text-pink-600 rounded focus:ring-pink-500 w-4 h-4 cursor-pointer" />
                                </label>
                                <label className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-100 p-1 -mx-1 rounded">
                                    <span className="text-gray-700">Show Location Details</span>
                                    <input type="checkbox" checked={shareSettings.showOverviewLocation} onChange={e => setShareSettings({...shareSettings, showOverviewLocation: e.target.checked})} className="text-pink-600 rounded focus:ring-pink-500 w-4 h-4 cursor-pointer" />
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Schedule & Timeline */}
                    <div className={`border rounded-lg overflow-hidden transition-colors ${expandedShareMenu === 'timeline' ? 'border-pink-300 ring-1 ring-pink-500/20' : 'border-gray-200 hover:border-pink-300'}`}>
                        <div className="flex items-center justify-between p-3 bg-white cursor-pointer" onClick={() => setExpandedShareMenu(expandedShareMenu === 'timeline' ? null : 'timeline')}>
                            <div className="flex-1 select-none">
                                <span className="font-bold text-gray-800 block text-sm">Schedule & Timeline</span>
                                <span className="text-xs text-gray-500">The day-of run of show.</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" checked={shareSettings.showTimeline} onChange={e => setShareSettings({...shareSettings, showTimeline: e.target.checked})} onClick={e => e.stopPropagation()} className="w-5 h-5 text-pink-600 focus:ring-pink-500 rounded cursor-pointer" />
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedShareMenu === 'timeline' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                        {expandedShareMenu === 'timeline' && (
                            <div className="p-3 bg-gray-50 border-t border-gray-100 space-y-3 animate-fade-in">
                                <label className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-100 p-1 -mx-1 rounded">
                                    <span className="text-gray-700">Show Private Notes</span>
                                    <input type="checkbox" checked={shareSettings.showTimelineNotes} onChange={e => setShareSettings({...shareSettings, showTimelineNotes: e.target.checked})} className="text-pink-600 rounded focus:ring-pink-500 w-4 h-4 cursor-pointer" />
                                </label>
                                <label className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-100 p-1 -mx-1 rounded">
                                    <span className="text-gray-700">Show Assigned Roles</span>
                                    <input type="checkbox" checked={shareSettings.showTimelineAssignees} onChange={e => setShareSettings({...shareSettings, showTimelineAssignees: e.target.checked})} className="text-pink-600 rounded focus:ring-pink-500 w-4 h-4 cursor-pointer" />
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Mood Board & Design */}
                    <div className={`border rounded-lg overflow-hidden transition-colors ${expandedShareMenu === 'design' ? 'border-pink-300 ring-1 ring-pink-500/20' : 'border-gray-200 hover:border-pink-300'}`}>
                        <div className="flex items-center justify-between p-3 bg-white cursor-pointer" onClick={() => setExpandedShareMenu(expandedShareMenu === 'design' ? null : 'design')}>
                            <div className="flex-1 select-none">
                                <span className="font-bold text-gray-800 block text-sm">Mood Board & Design</span>
                                <span className="text-xs text-gray-500">Inspiration and color palettes.</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" checked={shareSettings.showDesign} onChange={e => setShareSettings({...shareSettings, showDesign: e.target.checked})} onClick={e => e.stopPropagation()} className="w-5 h-5 text-pink-600 focus:ring-pink-500 rounded cursor-pointer" />
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedShareMenu === 'design' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                        {expandedShareMenu === 'design' && (
                            <div className="p-3 bg-gray-50 border-t border-gray-100 space-y-3 animate-fade-in">
                                <label className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-100 p-1 -mx-1 rounded">
                                    <span className="text-gray-700">Show Inspiration Images</span>
                                    <input type="checkbox" checked={shareSettings.showDesignImages} onChange={e => setShareSettings({...shareSettings, showDesignImages: e.target.checked})} className="text-pink-600 rounded focus:ring-pink-500 w-4 h-4 cursor-pointer" />
                                </label>
                                <label className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-100 p-1 -mx-1 rounded">
                                    <span className="text-gray-700">Show Color Palettes</span>
                                    <input type="checkbox" checked={shareSettings.showDesignColors} onChange={e => setShareSettings({...shareSettings, showDesignColors: e.target.checked})} className="text-pink-600 rounded focus:ring-pink-500 w-4 h-4 cursor-pointer" />
                                </label>
                            </div>
                        )}
                    </div>

                    {/* NEW: Travel & Accommodations */}
                    <div className={`border overflow-hidden transition-colors ${expandedShareMenu === 'travel' ? 'border-blue-300 ring-1 ring-blue-500/20 rounded-lg' : 'border-gray-200 hover:border-blue-300 rounded-lg'}`}>
                        <div className="flex items-center justify-between p-3 bg-white cursor-pointer" onClick={() => setExpandedShareMenu(expandedShareMenu === 'travel' ? null : 'travel')}>
                            <div className="flex-1 select-none">
                                <span className="font-bold text-gray-800 block text-sm">Travel & Accommodations</span>
                                <span className="text-xs text-gray-500">Room blocks and shuttles.</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" checked={shareSettings.showTravel} onChange={e => setShareSettings({...shareSettings, showTravel: e.target.checked})} onClick={e => e.stopPropagation()} className="w-5 h-5 text-blue-600 focus:ring-blue-500 rounded cursor-pointer" />
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedShareMenu === 'travel' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                    </div>

                    {/* NEW: Catering & Menu */}
                    <div className={`border overflow-hidden transition-colors ${expandedShareMenu === 'catering' ? 'border-orange-300 ring-1 ring-orange-500/20 rounded-lg' : 'border-gray-200 hover:border-orange-300 rounded-lg'}`}>
                        <div className="flex items-center justify-between p-3 bg-white cursor-pointer" onClick={() => setExpandedShareMenu(expandedShareMenu === 'catering' ? null : 'catering')}>
                            <div className="flex-1 select-none">
                                <span className="font-bold text-gray-800 block text-sm">Catering & Menu</span>
                                <span className="text-xs text-gray-500">Food, courses, and dishes.</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" checked={shareSettings.showCatering} onChange={e => setShareSettings({...shareSettings, showCatering: e.target.checked})} onClick={e => e.stopPropagation()} className="w-5 h-5 text-orange-600 focus:ring-orange-500 rounded cursor-pointer" />
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedShareMenu === 'catering' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                    </div>

                    {/* NEW: Checklist & Tasks */}
                    <div className={`border overflow-hidden transition-colors ${expandedShareMenu === 'checklist' ? 'border-emerald-300 ring-1 ring-emerald-500/20 rounded-lg' : 'border-gray-200 hover:border-emerald-300 rounded-lg'}`}>
                        <div className="flex items-center justify-between p-3 bg-white cursor-pointer" onClick={() => setExpandedShareMenu(expandedShareMenu === 'checklist' ? null : 'checklist')}>
                            <div className="flex-1 select-none">
                                <span className="font-bold text-gray-800 block text-sm">Checklist & Tasks</span>
                                <span className="text-xs text-gray-500">Share planning progress and to-dos.</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" checked={shareSettings.showChecklist} onChange={e => setShareSettings({...shareSettings, showChecklist: e.target.checked})} onClick={e => e.stopPropagation()} className="w-5 h-5 text-emerald-600 focus:ring-emerald-500 rounded cursor-pointer" />
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedShareMenu === 'checklist' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                        {expandedShareMenu === 'checklist' && shareSettings.showChecklist && (
                            <div className="bg-emerald-50/30 p-4 border-t border-emerald-100 flex flex-col gap-3">
                                <label className="flex items-center justify-between cursor-pointer p-2 hover:bg-white rounded-lg transition-colors">
                                    <span className="text-sm font-semibold text-gray-700">Allow Client Edits (Check off items)</span>
                                    <input type="checkbox" checked={shareSettings.allowChecklistEdits} onChange={e => setShareSettings({...shareSettings, allowChecklistEdits: e.target.checked})} className="w-4 h-4 text-emerald-600 rounded" />
                                </label>
                            </div>
                        )}
                    </div>

                    {/* NEW: Guest List Summary */}
                    <div className={`border overflow-hidden transition-colors ${expandedShareMenu === 'guests' ? 'border-indigo-300 ring-1 ring-indigo-500/20 rounded-lg' : 'border-gray-200 hover:border-indigo-300 rounded-lg'}`}>
                        <div className="flex items-center justify-between p-3 bg-white cursor-pointer" onClick={() => setExpandedShareMenu(expandedShareMenu === 'guests' ? null : 'guests')}>
                            <div className="flex-1 select-none">
                                <span className="font-bold text-gray-800 block text-sm">Guest List Summary</span>
                                <span className="text-xs text-gray-500">High-level RSVP numbers.</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" checked={shareSettings.showGuestSummary} onChange={e => setShareSettings({...shareSettings, showGuestSummary: e.target.checked})} onClick={e => e.stopPropagation()} className="w-5 h-5 text-indigo-600 focus:ring-indigo-500 rounded cursor-pointer" />
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedShareMenu === 'guests' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                    </div>

                    {/* NEW: Project Notes */}
                    <div className={`border overflow-hidden transition-colors ${expandedShareMenu === 'notes' ? 'border-yellow-300 ring-1 ring-yellow-500/20 rounded-lg' : 'border-gray-200 hover:border-yellow-300 rounded-lg'}`}>
                        <div className="flex items-center justify-between p-3 bg-white cursor-pointer" onClick={() => setExpandedShareMenu(expandedShareMenu === 'notes' ? null : 'notes')}>
                            <div className="flex-1 select-none">
                                <span className="font-bold text-gray-800 block text-sm">Project Notes</span>
                                <span className="text-xs text-gray-500">Share meeting notes and general details.</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" checked={shareSettings.showNotes} onChange={e => setShareSettings({...shareSettings, showNotes: e.target.checked})} onClick={e => e.stopPropagation()} className="w-5 h-5 text-yellow-600 focus:ring-yellow-500 rounded cursor-pointer" />
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedShareMenu === 'notes' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                        {expandedShareMenu === 'notes' && shareSettings.showNotes && (
                            <div className="bg-yellow-50/30 p-4 border-t border-yellow-100 flex flex-col gap-3">
                                <label className="flex items-center justify-between cursor-pointer p-2 hover:bg-white rounded-lg transition-colors">
                                    <span className="text-sm font-semibold text-gray-700">Allow Client to Add Notes</span>
                                    <input type="checkbox" checked={shareSettings.allowNotesEdits} onChange={e => setShareSettings({...shareSettings, allowNotesEdits: e.target.checked})} className="w-4 h-4 text-yellow-600 rounded" />
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Vendor Team */}
                    <div className={`border overflow-hidden transition-colors ${expandedShareMenu === 'vendors' ? 'border-yellow-400 bg-yellow-50/50 ring-1 ring-yellow-400/20 rounded-lg' : 'border-yellow-200 bg-yellow-50/30 hover:border-yellow-400 rounded-lg'}`}>
                        <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setExpandedShareMenu(expandedShareMenu === 'vendors' ? null : 'vendors')}>
                            <div className="flex-1 select-none">
                                <span className="font-bold text-gray-800 block text-sm">Vendor Team</span>
                                <span className="text-xs text-yellow-700">Vendor names and details.</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" checked={shareSettings.showVendors} onChange={e => setShareSettings({...shareSettings, showVendors: e.target.checked})} onClick={e => e.stopPropagation()} className="w-5 h-5 text-pink-600 focus:ring-pink-500 rounded cursor-pointer" />
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedShareMenu === 'vendors' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                        {expandedShareMenu === 'vendors' && (
                            <div className="p-3 bg-yellow-100/50 border-t border-yellow-200 space-y-3 animate-fade-in">
                                <label className="flex items-center justify-between text-sm cursor-pointer hover:bg-yellow-100/50 p-1 -mx-1 rounded">
                                    <span className="text-gray-800">Show Vendor Contact Info</span>
                                    <input type="checkbox" checked={shareSettings.showVendorContact} onChange={e => setShareSettings({...shareSettings, showVendorContact: e.target.checked})} className="text-pink-600 rounded focus:ring-pink-500 w-4 h-4 cursor-pointer" />
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Budget & Expenses */}
                    <div className={`border overflow-hidden transition-colors ${expandedShareMenu === 'budget' ? 'border-red-400 bg-red-50/50 ring-1 ring-red-400/20 rounded-lg' : 'border-red-200 bg-red-50/30 hover:border-red-400 rounded-lg'}`}>
                        <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setExpandedShareMenu(expandedShareMenu === 'budget' ? null : 'budget')}>
                            <div className="flex-1 select-none">
                                <span className="font-bold text-gray-800 block text-sm">Budget & Expenses</span>
                                <span className="text-xs text-red-600 font-medium italic">Warning: Shares financial details.</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <input type="checkbox" checked={shareSettings.showBudget} onChange={e => setShareSettings({...shareSettings, showBudget: e.target.checked})} onClick={e => e.stopPropagation()} className="w-5 h-5 text-pink-600 focus:ring-pink-500 rounded cursor-pointer" />
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedShareMenu === 'budget' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                        {expandedShareMenu === 'budget' && (
                            <div className="p-3 bg-red-100/50 border-t border-red-200 space-y-3 animate-fade-in">
                                <label className="flex items-center justify-between text-sm cursor-pointer hover:bg-red-100/50 p-1 -mx-1 rounded">
                                    <span className="text-gray-800">Show Estimated Budget</span>
                                    <input type="checkbox" checked={shareSettings.showBudgetEstimated} onChange={e => setShareSettings({...shareSettings, showBudgetEstimated: e.target.checked})} className="text-pink-600 rounded focus:ring-pink-500 w-4 h-4 cursor-pointer" />
                                </label>
                                <label className="flex items-center justify-between text-sm cursor-pointer hover:bg-red-100/50 p-1 -mx-1 rounded">
                                    <span className="text-gray-800 font-semibold text-red-800">Show Actual Money Spent</span>
                                    <input type="checkbox" checked={shareSettings.showBudgetActuals} onChange={e => setShareSettings({...shareSettings, showBudgetActuals: e.target.checked})} className="text-red-600 rounded focus:ring-red-500 w-4 h-4 cursor-pointer" />
                                </label>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 flex-shrink-0 flex-wrap">
                    <button onClick={() => setShowShareModal(false)} className="px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition w-full sm:w-auto">Cancel</button>
                    <button onClick={handlePreviewClientView} className="px-5 py-2.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg font-bold hover:bg-indigo-100 transition shadow-sm flex items-center justify-center gap-2 w-full sm:w-auto">
                        👁️ Preview
                    </button>
                    <button onClick={handleCopyShareLink} className="px-5 py-2.5 bg-gray-100 text-gray-800 border border-gray-200 rounded-lg font-bold hover:bg-gray-200 transition shadow-sm flex items-center justify-center gap-2 w-full sm:w-auto">
                        📋 Copy Link
                    </button>
                    <button onClick={handleNativeShare} className="px-5 py-2.5 bg-gray-900 text-white rounded-lg font-bold hover:bg-black transition shadow-md flex items-center justify-center gap-2 w-full sm:w-auto">
                        <PlannerIcons.Share /> Share via App
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Mobile Top Header */}
      <div className="md:hidden flex items-center justify-between py-2 px-4 bg-white border-b border-gray-200 shadow-sm z-20 flex-shrink-0">
        <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => safeNavigate('home')}>
          <span className="text-lg md:text-xl font-bold text-pink-500 italic tracking-tight">Host-it!</span>
          <span className="font-serif text-base md:text-lg font-bold text-gray-800 tracking-tight">Pro</span>
        </div>
        <div className="flex items-center gap-3">
          {/* <NotificationBell user={user} navigateTo={safeNavigate} /> */}
          <button onClick={() => setIsSidebarOpen(true)} className="p-1.5 -mr-1.5 text-gray-600 hover:text-gray-900 transition-colors">
            <PlannerIcons.Menu />
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/40 z-40 md:hidden backdrop-blur-sm transition-opacity print:hidden" 
          onClick={() => setIsSidebarOpen(false)} 
        />
      )}

      {/* SIDEBAR */}
      <aside className={`fixed inset-y-0 left-0 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 transition-all duration-300 ease-in-out w-72 ${isSidebarCollapsed ? 'md:w-20' : 'md:w-64'} bg-white border-r border-gray-200 flex flex-col shadow-[2px_0_15px_rgba(0,0,0,0.05)] z-50 md:z-10 flex-shrink-0 print:hidden`}>
        <div className={`h-16 flex items-center justify-between px-4 border-b border-gray-200 flex-shrink-0 ${isSidebarCollapsed ? 'md:flex-col md:justify-center md:gap-2 md:py-2 md:h-auto' : ''}`}>
          <div className={`flex items-center gap-1.5 cursor-pointer ${isSidebarCollapsed ? 'md:hidden' : ''}`} onClick={() => safeNavigate('home')}>
            <span className="text-2xl font-bold text-pink-500 italic tracking-tight hidden md:block">Host-it!</span>
            <span className="font-serif text-xl font-bold text-gray-800 tracking-tight hidden md:block">Pro</span>
            <span className="font-serif text-xl font-bold tracking-tight md:hidden">Menu</span>
          </div>
          
          <div className={`flex items-center gap-2 ${isSidebarCollapsed ? 'md:flex-col' : ''}`}>
              <div className="hidden md:block">
                  {/* <NotificationBell user={user} navigateTo={safeNavigate} dropdownAlign="left" /> */}
              </div>
              <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className="hidden md:block text-gray-400 hover:text-pink-600 transition-colors p-1 rounded-md hover:bg-gray-100">
                <PlannerIcons.Menu />
              </button>
              <button onClick={() => setIsSidebarOpen(false)} className="md:hidden text-gray-400 hover:text-gray-800 p-1">
                <PlannerIcons.X />
              </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto py-6 flex flex-col custom-scrollbar">
          <div className={`px-4 mb-2 ${isSidebarCollapsed ? 'md:px-2' : ''}`}>
            <p className={`text-[10px] font-bold tracking-widest text-gray-400 uppercase ml-2 mb-2 ${isSidebarCollapsed ? 'md:hidden' : ''}`}>Workspace</p>
            <button 
              onClick={() => { handleProjectSelect(null); setGlobalView('projects'); }}
              className={`w-full flex items-center py-2 rounded-md transition-colors ${!activeProjectId && globalView === 'projects' ? 'bg-pink-50 text-pink-700 font-bold' : 'text-gray-600 hover:bg-gray-50 font-medium'} gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0' : ''}`}
              title="Projects Dashboard"
            >
              <PlannerIcons.Grid /> <span className={`${isSidebarCollapsed ? 'md:hidden' : ''}`}>Projects Dashboard</span>
            </button>
            <button 
              onClick={() => { handleProjectSelect(null); setGlobalView('calendar'); }}
              className={`w-full flex items-center py-2 mt-1 rounded-md transition-colors ${!activeProjectId && globalView === 'calendar' ? 'bg-pink-50 text-pink-700 font-bold' : 'text-gray-600 hover:bg-gray-50 font-medium'} gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0' : ''}`}
              title="Master Calendar"
            >
              <PlannerIcons.CalendarIcon /> <span className={`${isSidebarCollapsed ? 'md:hidden' : ''}`}>Master Calendar</span>
            </button>
            <button 
              onClick={() => { handleProjectSelect(null); setGlobalView('services'); }}
              className={`w-full flex items-center py-2 mt-1 rounded-md transition-colors ${!activeProjectId && globalView === 'services' ? 'bg-pink-50 text-pink-700 font-bold' : 'text-gray-600 hover:bg-gray-50 font-medium'} gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0' : ''}`}
              title="My Planning Services"
            >
              <PlannerIcons.Star /> <span className={`${isSidebarCollapsed ? 'md:hidden' : ''}`}>My Services</span>
            </button>
            <button 
              onClick={() => { handleProjectSelect(null); setGlobalView('website'); }}
              className={`w-full flex items-center py-2 mt-1 rounded-md transition-colors ${!activeProjectId && globalView === 'website' ? 'bg-pink-50 text-pink-700 font-bold' : 'text-gray-600 hover:bg-gray-50 font-medium'} gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0' : ''}`}
              title="My Website"
            >
              <PlannerIcons.Globe /> <span className={`${isSidebarCollapsed ? 'md:hidden' : ''}`}>My Website</span>
            </button>
            <button 
              onClick={() => { handleProjectSelect(null); setGlobalView('vendor'); }}
              className={`w-full flex items-center py-2 mt-1 rounded-md transition-colors ${!activeProjectId && globalView === 'vendor' ? 'bg-pink-50 text-pink-700 font-bold' : 'text-gray-600 hover:bg-gray-50 font-medium'} gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0' : ''}`}
              title="Vendor Dashboard"
            >
              <PlannerIcons.Briefcase /> <span className={`${isSidebarCollapsed ? 'md:hidden' : ''}`}>Vendor Dashboard</span>
            </button>
          </div>

          <div className={`px-4 mt-6 flex-1 ${isSidebarCollapsed ? 'md:px-2' : ''}`}>
            <div className={`flex items-center mb-2 ${isSidebarCollapsed ? 'md:justify-center ml-0' : 'justify-between ml-2'}`}>
              <p className={`text-[10px] font-bold tracking-widest text-gray-400 uppercase ${isSidebarCollapsed ? 'md:hidden' : ''}`}>Active Weddings</p>
              <button onClick={handleCreateProject} className="text-gray-400 hover:text-pink-600 p-1 bg-white border border-gray-100 shadow-sm rounded-full hover:bg-gray-50" title="New Project"><PlannerIcons.Plus /></button>
            </div>
            <div className="space-y-1">
              {projects.map(p => (
                <button 
                  key={p.id}
                  onClick={() => handleProjectSelect(p.id)}
                  className={`w-full text-left flex items-center py-2.5 rounded-md transition-all ${activeProjectId === p.id ? 'bg-pink-50 text-pink-700 font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-50 font-medium'} gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0' : ''}`}
                  title={p.clientNames}
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${activeProjectId === p.id ? 'bg-pink-500' : 'bg-gray-300'}`} />
                  <span className={`truncate text-sm ${isSidebarCollapsed ? 'md:hidden' : ''}`}>{p.clientNames}</span>
                </button>
              ))}
            </div>
          </div>

          <div className={`px-4 mt-8 pt-6 border-t border-gray-100 ${isSidebarCollapsed ? 'md:px-2' : ''}`}>
            <p className={`text-[10px] font-bold tracking-widest text-pink-500 uppercase ml-2 mb-2 ${isSidebarCollapsed ? 'md:hidden' : ''}`}>Host-it! Platform</p>
            <div className="space-y-1">
                <button onClick={() => safeNavigate('home')} className={`w-full flex items-center py-2 text-gray-600 hover:text-pink-600 hover:bg-pink-50 rounded-md transition-colors text-sm font-medium gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0' : ''}`} title="Home">
                  <span className="text-lg">🏠</span> <span className={`${isSidebarCollapsed ? 'md:hidden' : ''}`}>Home</span>
                </button>
                <button onClick={() => safeNavigate('listings')} className={`w-full flex items-center py-2 text-gray-600 hover:text-pink-600 hover:bg-pink-50 rounded-md transition-colors text-sm font-medium gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0' : ''}`} title="Browse Marketplace">
                  <PlannerIcons.Storefront /> <span className={`${isSidebarCollapsed ? 'md:hidden' : ''}`}>Browse Marketplace</span>
                </button>
                <button onClick={() => safeNavigate('messages')} className={`w-full flex items-center py-2 text-gray-600 hover:text-pink-600 hover:bg-pink-50 rounded-md transition-colors text-sm font-medium gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0' : ''}`} title="Messages & Inbox">
                  <PlannerIcons.Chat /> <span className={`${isSidebarCollapsed ? 'md:hidden' : ''}`}>Messages & Inbox</span>
                </button>
                <button onClick={() => safeNavigate('dashboard')} className={`w-full flex items-center py-2 text-gray-600 hover:text-pink-600 hover:bg-pink-50 rounded-md transition-colors text-sm font-medium gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0' : ''}`} title="Main Dashboard">
                  <PlannerIcons.Document /> <span className={`${isSidebarCollapsed ? 'md:hidden' : ''}`}>Main Dashboard</span>
                </button>
            </div>
          </div>
        </div>

        <div className={`p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 flex flex-col gap-2 ${isSidebarCollapsed ? 'md:p-2 md:items-center' : ''}`}>
          <button onClick={() => safeNavigate('settings')} className={`w-full flex items-center py-2 text-gray-800 hover:bg-white rounded-md transition-colors shadow-sm border border-gray-200 gap-3 px-3 ${isSidebarCollapsed ? 'md:justify-center md:px-0 md:bg-transparent md:border-none md:shadow-none' : ''}`} title="Settings">
            <img src={user?.profilePicUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.plannerName || user?.email || 'User')}&background=fdf2f8&color=db2777`} className={`rounded-full object-cover ${isSidebarCollapsed ? 'w-8 h-8' : 'w-8 h-8 md:w-6 md:h-6'}`} alt="Profile" />
            <div className={`text-left flex-1 min-w-0 ${isSidebarCollapsed ? 'md:hidden' : ''}`}>
              <span className="font-bold text-sm block truncate">{user?.plannerName || user?.email || 'Planner'}</span>
              <span className="text-[10px] text-pink-600 font-bold tracking-wide uppercase block">Settings</span>
            </div>
            <span className={`${isSidebarCollapsed ? 'md:hidden' : ''}`}>
                <PlannerIcons.Settings />
            </span>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 flex flex-col min-w-0 bg-gray-50 overflow-hidden relative">
        {!activeProject ? (
          globalView === 'vendor' ? (
            <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
                <VendorDashboard
                  user={user}
                  db={db}
                  storage={storage}
                  functions={functions}
                  navigateTo={navigateTo}
                  initialData={initialData}
                  isMapsScriptLoaded={isMapsScriptLoaded}
                  isUserAuthenticated={isUserAuthenticated}
                />
            </div>
          ) : globalView === 'calendar' ? (
            <PlannerGlobalCalendar projects={projects} onSelectProject={handleProjectSelect} updateProject={updateProject} />
          ) : globalView === 'services' ? (
            <PlannerServicesTab user={user} db={db} storage={storage} isMapsScriptLoaded={isMapsScriptLoaded} />
          ) : globalView === 'website' ? (
            <div className="flex-1 overflow-hidden h-full">
                <PlannerWebsiteEditor user={user} db={db} storage={storage} navigateTo={navigateTo} />
            </div>
          ) : (
            <GlobalDashboard projects={projects} onSelectProject={handleProjectSelect} onCreateProject={handleCreateProject} onDeleteProject={setProjectToDelete} navigateTo={safeNavigate} />
          )
        ) : (
          <>
            {/* PROJECT HEADER */}
            <header className="bg-white border-b border-gray-200 shadow-sm z-10 flex-shrink-0 print:hidden relative transition-all duration-300">
              <div className={`relative group transition-all duration-500 ease-in-out origin-top overflow-hidden ${isScrolled ? 'h-0 opacity-0' : 'h-40 md:h-48 opacity-100'}`}>
                <img src={activeProject.coverImage} className="w-full h-full object-cover" alt="Cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/30 to-transparent" />
                <div className="absolute bottom-0 left-0 w-full p-4 lg:p-6 flex flex-col lg:flex-row lg:justify-between lg:items-end gap-4 z-10">
                  <div className="flex-1 min-w-0 pr-4 text-white">
                    <h1 className="text-2xl md:text-4xl font-serif font-bold tracking-wide drop-shadow-md line-clamp-2 md:line-clamp-3 leading-tight break-words" title={activeProject.clientNames}>{activeProject.clientNames}</h1>
                    <p className="text-xs md:text-sm font-medium mt-1.5 drop-shadow flex flex-wrap items-center gap-2 md:gap-4 opacity-90">
                      <span className="bg-black/30 backdrop-blur-md px-2.5 py-1 rounded-full">{activeProject.date !== 'TBD' ? new Date(activeProject.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'Date TBD'}</span>
                      <span className="bg-black/30 backdrop-blur-md px-2.5 py-1 rounded-full truncate max-w-[150px] sm:max-w-[300px]">{activeProject.location}</span>
                      {activeProject.venueName && (
                          <span className="bg-black/30 backdrop-blur-md px-2.5 py-1 rounded-full truncate max-w-[150px] sm:max-w-[300px]">
                              {activeProject.venueAddress ? (
                                  <a href={`https://maps.google.com/?q=${encodeURIComponent(activeProject.venueAddress)}`} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1 text-white">
                                      📍 {activeProject.venueName}
                                  </a>
                              ) : (
                                  <span className="flex items-center gap-1">📍 {activeProject.venueName}</span>
                              )}
                          </span>
                      )}
                    </p>
                  </div>
                  {/* Cleaned up Edit Buttons for Mobile */}
                  <div className="flex flex-wrap gap-2 flex-shrink-0">
                      <button onClick={() => safeNavigate('listings')} className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition flex items-center gap-2 shadow-sm"><PlannerIcons.Storefront /> Marketplace</button>
                      <button onClick={handleOpenShare} className="bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/30 text-white px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition flex items-center gap-2 shadow-sm"><PlannerIcons.Share /> Share View</button>
                      <button onClick={handleEditDetails} className="bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/30 text-white px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition flex items-center gap-2 shadow-sm"><PlannerIcons.Settings /> Edit Details</button>
                      <button onClick={handleEditCover} className="bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/30 text-white px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition items-center gap-2 shadow-sm hidden sm:flex"><PlannerIcons.Palette /> Cover</button>
                  </div>
                </div>
              </div>
              
              <div className="px-4 md:px-8 py-3 bg-gray-50/95 backdrop-blur-md border-t border-gray-200 flex items-end overflow-x-auto custom-scrollbar no-scrollbar-mobile">
                {/* Compact Title when scrolled */}
                <div className={`transition-all duration-300 flex items-center flex-shrink-0 mb-1 ${isScrolled ? 'opacity-100 pr-4 mr-4 border-r border-gray-300 visible' : 'w-0 opacity-0 pr-0 mr-0 border-transparent invisible overflow-hidden'}`}>
                    <h2 className="font-serif font-bold text-gray-900 truncate max-w-[150px] sm:max-w-[250px] lg:max-w-[400px] text-lg" title={activeProject.clientNames}>{activeProject.clientNames}</h2>
                    <button onClick={() => setIsScrolled(false)} className="ml-2 text-gray-400 hover:text-pink-600 transition-colors bg-white rounded-full p-0.5 shadow-sm" title="Show Cover">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                </div>

                <div className="flex gap-6 items-end">
                  {PLANNER_CATEGORIES.map((category, idx) => {
                    const enabledTabs = enabledTabsToUse;
                    const visibleTabs = category.tabs.filter(tab => enabledTabs.includes(tab.id));
                    
                    if (visibleTabs.length === 0) return null;

                    return (
                        <div key={idx} className="flex flex-col gap-2 flex-shrink-0">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">{category.group}</span>
                        <div className="flex bg-white rounded-xl shadow-sm border border-gray-200 p-1 gap-1">
                            {visibleTabs.map(tab => {
                            const IconComponent = tab.icon;
                            return (
                                <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 font-bold text-[10px] sm:text-sm rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${activeTab === tab.id ? 'bg-pink-50 text-pink-700 shadow-sm ring-1 ring-pink-200' : 'bg-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                                >
                                <span className={`${activeTab === tab.id ? 'text-pink-600' : 'text-gray-400'}`}>
                                    {IconComponent && <IconComponent />}
                                </span> 
                                <span>{tab.label}</span>
                                </button>
                            );
                            })}
                        </div>
                        </div>
                    );
                  })}
                </div>
              </div>
            </header>

            {/* PROJECT TABS */}
            <div 
              className="flex-1 overflow-y-auto p-4 md:p-8"
              onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.target;
                  if (scrollTop > 150 && !isScrolled && (scrollHeight - clientHeight > 300)) {
                      setIsScrolled(true);
                  } else if (scrollTop <= 10 && isScrolled) {
                      setIsScrolled(false);
                  }
              }}
            >
              <div className="max-w-6xl mx-auto animate-fade-in pb-12">
                {activeTab === 'Overview' && <ProjectOverview project={activeProject} safeNavigate={safeNavigate} />}
                {activeTab === 'Checklist' && <ProjectChecklist project={activeProject} updateProject={updateProject} />}
                {activeTab === 'Budget' && <ProjectBudget project={activeProject} updateProject={updateProject} safeNavigate={safeNavigate} />}
                {activeTab === 'Time' && <ProjectTimeTracker project={activeProject} updateProject={updateProject} />}
                {activeTab === 'VIPs' && <ProjectVIPs project={activeProject} updateProject={updateProject} />}
                {activeTab === 'Guests' && <ProjectGuests project={activeProject} updateProject={updateProject} safeNavigate={safeNavigate} />}
                {activeTab === 'Seating' && <ProjectSeating project={activeProject} updateProject={updateProject} />}
                {activeTab === 'Catering' && <ProjectCatering project={activeProject} updateProject={updateProject} />}
                {activeTab === 'Vendors' && <ProjectVendors project={activeProject} updateProject={updateProject} safeNavigate={safeNavigate} />}
                {activeTab === 'Rentals' && <ProjectRentals project={activeProject} user={user} db={db} safeNavigate={safeNavigate} />}
                {activeTab === 'Travel' && <ProjectTravel project={activeProject} updateProject={updateProject} />}
                {activeTab === 'Timeline' && <ProjectTimeline project={activeProject} updateProject={updateProject} />}
                {activeTab === 'Packing' && <ProjectPacking project={activeProject} updateProject={updateProject} />}
                {activeTab === 'Design' && <ProjectDesign project={activeProject} updateProject={updateProject} safeNavigate={safeNavigate} user={user} storage={storage} />}
                {activeTab === 'Gifts' && <ProjectGifts project={activeProject} updateProject={updateProject} />}
                {activeTab === 'Vault' && <ProjectVault project={activeProject} updateProject={updateProject} />}
                {activeTab === 'Notes' && <ProjectNotes project={activeProject} updateProject={updateProject} />}
              </div>
            </div>
          </>
        )}
      </main>

      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        @media (max-width: 768px) {
            .no-scrollbar-mobile::-webkit-scrollbar { display: none; }
            .no-scrollbar-mobile { -ms-overflow-style: none; scrollbar-width: none; }
        }
        @media print {
            body { background: white !important; }
            .animate-fade-in { animation: none !important; }
            .print\:hidden { display: none !important; }
            .print\:border-none { border: none !important; }
            .print\:shadow-none { box-shadow: none !important; }
            .print\:p-0 { padding: 0 !important; }
            .print\:pb-4 { padding-bottom: 1rem !important; }
            .print\:border-b { border-bottom-width: 1px !important; }
            .print\:bg-transparent { background-color: transparent !important; }
            .print\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
            .print\:text-gray-800 { color: #1f2937 !important; }
        }
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: none; } }
      `}} />
    </div>
  );
}